import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "./../notification.hook";
import { RefreshReactQuery } from "../../constants/AppConstants";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import { UpdateCollaboratorsCmSheetFinalInterviewRequest } from "../../types/api/collaborators.type";
import { useEffect } from "react";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";
import CmSheetFinalInterviewService from "../../services/api/cmSheet/CmSheetFinalInterview.service";

const CM_SHEET_FINAL_INTERVIEW_QUERY_KEY = "get-cm-sheet-final-interview";

export const useCmSheetFinalInterview = (cmSheetId: number, enabled = true) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_FINAL_INTERVIEW_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetFinalInterviewService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetFinalInterview = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateFinalInterview, isPending: isUpdating } =
    useMutation({
      mutationFn: (body: UpdateCollaboratorsCmSheetFinalInterviewRequest) =>
        CmSheetFinalInterviewService.update(cmSheetId, body),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_FINAL_INTERVIEW_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
          }),
        ]);
        notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
      },
      onError: (error) => {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      },
    });

  return { updateFinalInterview, isUpdating };
};
